import React, { useEffect, useMemo, useRef, useState } from 'react';
import { selectedAccStateSelector } from '@core/store/library-stores/selectors';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { Button, logout, selectUserProfileId, styleConstants } from '@hwm/ui-lib';
import { Trans } from '@lingui/macro';
import { makeStyles, Menu, Theme } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import PersonIcon from '@material-ui/icons/Person';
import { environment } from '@root/environment';
import { useUserDetailsDataSource } from './userDetailsDataSource';
import { isNil } from 'lodash';
import { accountsReducer } from '@core/store/library-stores/accounts';

export const useStyles = makeStyles((theme: Theme) => ({
  userDetailsMenuWrapper: {
    top: '66px !important',
    left: 'unset !important',
    right: 0,
  },
  userDetailsMenuListWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'column',
    padding: '24px',
    color: styleConstants.colors.grey,
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '16px',
    '& > *:not(:last-child)': {
      marginBottom: '8px',
    },
  },
  version: {
    fontSize: '12px',
    textAlign: 'right',
    width: '100%',
    color: '#B6BCCE',
    borderTop: '1px solid #B6BCCE',
    paddingTop: '7px',
    marginTop: '13px',
  },
}));

export const UserDetails: React.FC = () => {
  const [openUserDetails, setOpenUserDetails] = useState<boolean>(false);
  const userDetailsRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const { userMainAccount } = useAppSelector(selectedAccStateSelector);

  const userId: number = useAppSelector(selectUserProfileId);

  const { userAccessProfile, getUserAccessProfile, loading, error } = useUserDetailsDataSource();

  useEffect(() => {
    if (!isNil(userId)) {
      getUserAccessProfile(userId);
    }
  }, [userId, getUserAccessProfile]);

  const userRole = useMemo(
    () =>
      userAccessProfile
        ? userAccessProfile?.roles?.find((role) => role.applicationName === environment.appName)?.roleName || null
        : '',
    [userAccessProfile]
  );

  useEffect(() => {
    if (userAccessProfile && !loading && !isNil(userId) && !error) {
      dispatch(accountsReducer.slice.actions.setUserRole(userRole));
    }
  }, [dispatch, userAccessProfile, userId, userRole, loading, error]);

  return (
    <div ref={userDetailsRef}>
      <Button
        variant="outlined"
        onClick={() => setOpenUserDetails(!openUserDetails)}
        style={{ fontSize: '16px' }}
        endIcon={openUserDetails ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        startIcon={<PersonIcon />}
      >
        {userMainAccount?.userName}
      </Button>
      <Menu
        open={openUserDetails}
        anchorEl={userDetailsRef?.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClick={() => setOpenUserDetails(false)}
        classes={{
          paper: classes.userDetailsMenuWrapper,
          list: classes.userDetailsMenuListWrapper,
        }}
      >
        <div>
          <Trans>Parent Account: {userMainAccount?.name}</Trans>
        </div>
        <div>
          <Trans>User Level: {loading ? '' : userRole}</Trans>
        </div>

        <Button
          variant="outlined"
          onClick={() => {
            logout({
              sessionCookieDomain: environment.sessionCookieDomain,
              sessionCookieIsSecure: environment.sessionCookieSecure,
              sessionIdleTimeout: environment.sessionCookieIdleTimeout,
            });
          }}
          style={{ fontSize: '16px' }}
        >
          <Trans>Log out</Trans>
        </Button>
        <div className={classes.version}>
          <Trans comment="Application version">Version</Trans>: {environment.version}
        </div>
      </Menu>
    </div>
  );
};
